import React, { useMemo } from 'react';
import styled from 'styled-components';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { phoneNumberToLink } from 'Common/helpers/phoneNumber';
import { FooterLinks } from 'OneWestwingFooter/types';
import useConfig from 'AppShell/hooks/useConfig';
import ClimateNeutral from 'OneWestwingFooter/ClimateNeutral';
import { body2 } from '@westwing/ui-kit/typography';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { ensureEndingSlash } from 'Client/urlService';
import paths from 'Common/routes/paths';

const Phone = styled.div`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const AvailabilityHours = styled.div`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal60};
`;

const PhoneAndAvailabilityHoursWrapper = styled.div`
    display: flex;
    flex-direction: column;

    &:hover ${Phone} {
        ${forDesktop`
            color: ${({ theme }) => theme.colors.charcoal50};
        `}
    }
`;

// AvailabilityHours uses dangerouslySetInnerHTML due to some pokeys containing HTML like <br>
const PhoneAndAvailability: React.FC<{ phoneLabel: string; availabilityLabel: string }> = ({
    phoneLabel,
    availabilityLabel,
}) => (
    <PhoneAndAvailabilityHoursWrapper>
        <Phone>{phoneLabel}</Phone>
        <AvailabilityHours dangerouslySetInnerHTML={{ __html: availabilityLabel }} />
    </PhoneAndAvailabilityHoursWrapper>
);

const useFooterUsefulLinks = (isClub: boolean = false) => {
    const config = useConfig();
    const { links } = config;
    const t = useBellaTranslator();

    const { helpCenterUrl, helpCenterContactUrl, umweltUndEntsorgung } = isClub ? links.clubPaths : links;

    const seoLinks = useMemo(() => {
        const platformLinks = !isClub ? links.clubPaths : links;
        return [
            {
                to: platformLinks.helpCenterUrl,
                text: t('Footer_FAQ'),
            },
            {
                to: platformLinks.helpCenterContactUrl,
                text: t('Contact Form'),
            },
            {
                to: platformLinks.umweltUndEntsorgung,
                text: t('Footer_Umwelt_und_Entsorgung'),
            },
            {
                to: !isClub ? platformLinks.sales : links.brands,
                test: t('Footer_Brands'),
            },
        ];
    }, [isClub, links, t]);

    const featureToggles = useFeatureToggle();

    const contactLinks = useMemo<FooterLinks>(
        () => ({
            title: t('Contacts_and_FAQ'),
            key: 'footer_title_Contacts_and_FAQ',
            accordionTrackingActions: {
                open: 'Help_Contact - open',
                close: 'Help_Contact - close',
            },
            items: [
                {
                    to: phoneNumberToLink(t('Phone number')),
                    content: (
                        <PhoneAndAvailability
                            phoneLabel={t('Phone number')}
                            availabilityLabel={t('Availability_hours')}
                        />
                    ),
                    trackingAction: 'Phone Number - click',
                },
                {
                    to: helpCenterContactUrl,
                    content: t('Contact Form'),
                    trackingAction: 'Contact Form - click',
                },
                { to: helpCenterUrl, content: t('Footer_FAQ'), trackingAction: 'FAQ - click' },
            ],
        }),
        [helpCenterContactUrl, helpCenterUrl, t]
    );

    const aboutWestwingLinks = useMemo<FooterLinks>(
        () => ({
            title: t('Footer_about_Westwing'),
            key: 'footer_title_Footer_about_Westwing',
            accordionTrackingActions: {
                open: 'About Westwing - open',
                close: 'About Westwing - close',
            },
            items: [
                {
                    to: links.career,
                    content: t('Footer_Career'),
                    trackingAction: 'Career - click',
                    openInNewTab: true,
                },
                {
                    to: links.corporate,
                    content: t('Footer_Corporate_Website'),
                    trackingAction: 'Corporate Website - click',
                    openInNewTab: true,
                },
                ...(links.compliance
                    ? [
                          {
                              to: links.compliance,
                              content: t('Footer_compliance'),
                              trackingAction: 'Compliance - click',
                              openInNewTab: true,
                          },
                      ]
                    : []),
                ...(links.dispute
                    ? [
                          {
                              to: links.dispute,
                              content: t('Footer_Dispute resolution'),
                              trackingAction: 'Dispute resolution - click',
                              openInNewTab: true,
                          },
                      ]
                    : []),
                ...(links.ODR
                    ? [
                          {
                              to: links.ODR,
                              content: t('Footer_ODR'),
                              trackingAction: 'ODR - click',
                              openInNewTab: true,
                          },
                      ]
                    : []),
                ...(isClub && links.legal
                    ? [
                          {
                              to: links.legal,
                              content: t('Footer_Legal information and terms'),
                              trackingAction: 'Legal information and terms - click',
                              openInNewTab: true,
                          },
                      ]
                    : []),
                ...(links.owwPress
                    ? [
                          {
                              to: links.owwPress,
                              content: t('Footer_Press'),
                              trackingAction: 'Press - click',
                          },
                      ]
                    : []),

                ...(!isClub
                    ? [
                          {
                              to: links.brands,
                              content: t('Footer_Brands'),
                              trackingAction: 'Marken - click',
                          },
                      ]
                    : []),
                ...(links.taxStrategy
                    ? [
                          {
                              to: links.taxStrategy['2021'],
                              content: 'Strategia podatkowa 2021', // A legal requirement only in PL
                              openInNewTab: true,
                          },
                          {
                              to: links.taxStrategy['2022'],
                              content: 'Strategia podatkowa 2022', // A legal requirement only in PL
                              openInNewTab: true,
                          },
                      ]
                    : []),
            ],
        }),
        [
            isClub,
            links.ODR,
            links.brands,
            links.career,
            links.clubPaths.sales,
            links.compliance,
            links.corporate,
            links.dispute,
            links.legal,
            links.owwPress,
            t,
        ]
    );

    const informationLinks = useMemo<FooterLinks>(
        () => ({
            title: t('Footer_Useful_information'),
            key: 'footer_title_Footer_Useful_information',
            accordionTrackingActions: {
                open: 'Useful information - open',
                close: 'Useful information - close',
            },
            items: [
                ...(links.b2b
                    ? [
                          {
                              to: links.b2b,
                              content: t('Footer_B2B'),
                              trackingAction: 'B2B - click',
                          },
                      ]
                    : []),
                ...(featureToggles.interiorServiceInNav
                    ? [
                          {
                              to: ensureEndingSlash(paths.INTERIOR_DESIGN_SERVICE),
                              content: t('Footer_Interior_service'),
                              trackingAction: 'Beratung - click',
                          },
                      ]
                    : []),
                {
                    to: links.clubPaths.inspiration,
                    content: t('Footer_Style_Hub'),
                    trackingAction: 'Inspiration - click',
                },
                ...(umweltUndEntsorgung
                    ? [
                          {
                              to: umweltUndEntsorgung,
                              content: t('Footer_Umwelt_und_Entsorgung'),
                              trackingAction: 'Umwelt_und_Entsorgung - click',
                          },
                      ]
                    : []),
            ],
        }),
        [featureToggles.interiorServiceInNav, links.b2b, links.clubPaths.inspiration, t, umweltUndEntsorgung]
    );

    const sustainabilityLinks = useMemo<FooterLinks>(
        () => ({
            title: t('Footer_Sustainability'),
            key: 'footer_title_Footer_Sustainability',
            accordionTrackingActions: {
                open: 'Sustainability - open',
                close: 'Sustainability - close',
            },
            items: [
                {
                    to: links.sustainability,
                    content: t('Footer_Sustainability_promise'),
                    trackingAction: 'Sustainabilty_promise - click',
                    openInNewTab: true,
                },
                {
                    to: links.insights,
                    content: t('Footer_Insights_Stories'),
                    trackingAction: 'Insights_Stories - click',
                    openInNewTab: true,
                },
                {
                    content: <ClimateNeutral />,
                },
            ],
        }),
        [links.insights, links.sustainability, t]
    );

    return { usefulLinks: [contactLinks, aboutWestwingLinks, informationLinks, sustainabilityLinks], seoLinks };
};

export default useFooterUsefulLinks;
